/** Hosted Sites page — upload, browse, preview & share static HTML/ZIP sites */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Upload, Search, Globe, ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '../lib/utils'
import { useAppStore } from '../stores/app-store'
import { useHostedSites, useUploadSite, useUpdateSite, useDeleteSite, useRegenerateSiteSummary } from '../hooks/use-hosted-sites'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import { HostedSiteRow } from '../components/hosted-sites/hosted-site-row'
import { HostedSiteCard } from '../components/hosted-sites/hosted-site-card'
import { UploadPanel } from '../components/hosted-sites/upload-panel'
import { ViewToggle } from '../components/hosted-sites/view-toggle'
import { FilterBar } from '../components/hosted-sites/filter-bar'
import { sitePublicUrl, DESCRIPTION_MAX } from '../components/hosted-sites/utils'
import type { HostedSite } from '../hooks/use-hosted-sites'
import type { DateRange } from '../components/hosted-sites/filter-bar'

// Preset ms offsets for date-range filter
const PRESET_MS: Record<Exclude<DateRange, 'all'>, number> = {
  '7d': 7 * 86_400e3,
  '30d': 30 * 86_400e3,
  '90d': 90 * 86_400e3,
}

// Whitelist URL filter values — fall back to '' on garbage to prevent stuck-loading 400s
const VALID_STATUSES = ['ready', 'processing', 'failed'] as const
const VALID_SITE_TYPES = ['html', 'zip'] as const
const VALID_DATE_RANGES = ['all', '7d', '30d', '90d'] as const
type Status = (typeof VALID_STATUSES)[number]
type SiteType = (typeof VALID_SITE_TYPES)[number]

export function HostedSitesPage() {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // --- URL-persisted filter state — validate so stray ?status=foo doesn't break the API call ---
  const [searchParams, setSearchParams] = useSearchParams()
  const rawStatus = searchParams.get('status')
  const rawSiteType = searchParams.get('siteType')
  const rawDateRange = searchParams.get('dateRange')
  const status = (VALID_STATUSES as readonly string[]).includes(rawStatus ?? '') ? (rawStatus as Status) : ''
  const siteType = (VALID_SITE_TYPES as readonly string[]).includes(rawSiteType ?? '') ? (rawSiteType as SiteType) : ''
  const dateRange: DateRange = (VALID_DATE_RANGES as readonly string[]).includes(rawDateRange ?? '')
    ? (rawDateRange as DateRange)
    : 'all'

  function applyFilters(filters: { status: string; siteType: string; dateRange: DateRange }) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      // Preserve existing ?q= search param
      if (filters.status) next.set('status', filters.status)
      else next.delete('status')
      if (filters.siteType) next.set('siteType', filters.siteType)
      else next.delete('siteType')
      if (filters.dateRange !== 'all') next.set('dateRange', filters.dateRange)
      else next.delete('dateRange')
      return next
    })
  }

  // --- View pref — localStorage, default list ---
  const [view, setView] = useState<'list' | 'card'>(
    () => (localStorage.getItem('hosted-sites-view') as 'list' | 'card' | null) ?? 'list',
  )
  useEffect(() => { localStorage.setItem('hosted-sites-view', view) }, [view])

  // --- Local search state (not in URL to avoid debounce complexity) ---
  const [search, setSearch] = useState('')

  // --- Component-local UI state ---
  const [description, setDescription] = useState('')
  const [autoSummary, setAutoSummary] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [previewSite, setPreviewSite] = useState<HostedSite | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<HostedSite | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [summaryFull, setSummaryFull] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const cancelledRef = useRef(false)

  const toggleExpanded = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
    setSummaryFull(false)
    setEditingId(null)
  }, [])

  // Build filter object for the hook — memoized so queryKey is referentially stable
  // across renders; otherwise Date.now() recomputes per render → infinite refetch and
  // cache invalidation in useUpdateSite cannot find the matching key.
  const filters = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      siteType: siteType || undefined,
      createdAfter: dateRange !== 'all' ? Date.now() - PRESET_MS[dateRange] : undefined,
    }),
    [search, status, siteType, dateRange],
  )

  const { data, isLoading } = useHostedSites(filters)
  const uploadSite = useUploadSite()
  const updateSite = useUpdateSite(filters)
  const deleteSite = useDeleteSite()
  const regenerateSummary = useRegenerateSiteSummary()
  const [pendingSummaryId, setPendingSummaryId] = useState<string | null>(null)
  const [summaryError, setSummaryError] = useState<{ id: string; message: string } | null>(null)
  const pendingBaselineRef = useRef<string | null>(null)

  const startEdit = useCallback((site: HostedSite) => {
    cancelledRef.current = false
    setEditingId(site.id)
    setDraft(site.description ?? '')
  }, [])

  const cancelEdit = useCallback(() => {
    cancelledRef.current = true
    setEditingId(null)
    setDraft('')
  }, [])

  const commitEdit = useCallback(
    (site: HostedSite) => {
      if (cancelledRef.current) { cancelledRef.current = false; return }
      if (editingId !== site.id) return
      const trimmed = draft.trim()
      const next = trimmed.length === 0 ? null : trimmed.slice(0, DESCRIPTION_MAX)
      const current = site.description ?? null
      setEditingId(null)
      setDraft('')
      if (next === current) return
      updateSite.mutate(
        { id: site.id, description: next },
        { onError: (err) => toast.error('Failed to update description', { description: err instanceof Error ? err.message : undefined }) },
      )
    },
    [editingId, draft, updateSite],
  )

  const sites = data?.sites ?? []
  const total = data?.total ?? 0

  const handleRegenerate = useCallback(async (id: string) => {
    setSummaryError(null)
    pendingBaselineRef.current = sites.find((s) => s.id === id)?.summary ?? null
    setPendingSummaryId(id)
    try {
      await regenerateSummary.mutateAsync(id)
    } catch (err) {
      setSummaryError({ id, message: err instanceof Error ? err.message : 'Failed to generate summary' })
      setPendingSummaryId(null)
    }
  }, [regenerateSummary, sites])

  // Clear pending spinner once refetch surfaces a new summary; hard-cap at 30s
  useEffect(() => {
    if (!pendingSummaryId) return
    const current = sites.find((s) => s.id === pendingSummaryId)
    if (current?.summary && current.summary !== pendingBaselineRef.current) {
      setPendingSummaryId(null)
      pendingBaselineRef.current = null
      return
    }
    const cap = setTimeout(() => setPendingSummaryId(null), 30_000)
    return () => clearTimeout(cap)
  }, [pendingSummaryId, sites])

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await uploadSite.mutateAsync({ file, description: description || undefined, autoSummary: autoSummary || undefined })
      setDescription('')
      setAutoSummary(false)
      setShowUpload(false)
    } catch { /* error surfaced via mutation state */ }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [uploadSite, description, autoSummary])

  const handleCopyLink = useCallback((site: HostedSite) => {
    void navigator.clipboard.writeText(sitePublicUrl(site))
    setCopiedId(site.id)
    setTimeout(() => setCopiedId(null), 2000)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    await deleteSite.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, deleteSite])

  const hasActiveFilters = status !== '' || siteType !== '' || dateRange !== 'all'

  const handleTogglePreview = useCallback(
    (site: HostedSite) => setPreviewSite((prev) => (prev?.id === site.id ? null : site)),
    [],
  )

  // Shared props forwarded to each row/card — memoized so unchanged rows don't re-render
  // when unrelated state (e.g. another row's edit) changes.
  const sharedSiteProps = useMemo(
    () => ({
      isDark,
      expandedId,
      toggleExpanded,
      editingId,
      draft,
      setDraft,
      summaryFull,
      setSummaryFull,
      pendingSummaryId,
      summaryError,
      copiedId,
      previewSite,
      onCopyLink: handleCopyLink,
      onTogglePreview: handleTogglePreview,
      onDelete: setDeleteTarget,
      onStartEdit: startEdit,
      onCancelEdit: cancelEdit,
      onCommitEdit: commitEdit,
      onRegenerate: handleRegenerate,
    }),
    [
      isDark, expandedId, toggleExpanded, editingId, draft, summaryFull,
      pendingSummaryId, summaryError, copiedId, previewSite,
      handleCopyLink, handleTogglePreview, startEdit, cancelEdit, commitEdit, handleRegenerate,
    ],
  )

  return (
    <div className={cn('flex h-screen flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Top bar */}
      <div className={cn('flex items-center gap-3 border-b px-4 py-3', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}>
        <button
          onClick={() => navigate(-1)}
          className={cn('rounded-md p-1', isDark ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700')}
          title="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Globe className={cn('h-4 w-4', isDark ? 'text-brand-400' : 'text-brand-600')} />
        <h1 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
          Hosted Sites
        </h1>
        <span className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          {total} {total === 1 ? 'site' : 'sites'}
        </span>
        <div className="flex-1" />

        {/* Filter bar */}
        <FilterBar
          status={status}
          siteType={siteType}
          dateRange={dateRange}
          onChange={applyFilters}
          isDark={isDark}
        />

        {/* View toggle */}
        <ViewToggle view={view} onChange={setView} isDark={isDark} />

        {/* Search */}
        <div className="relative">
          <Search className={cn('absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'w-28 rounded-lg border py-1.5 pl-8 pr-3 text-xs outline-none sm:w-44',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 focus:border-brand-500',
            )}
          />
        </div>

        {/* Upload button */}
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
        >
          <Upload className="h-3.5 w-3.5" />
          Upload Site
        </button>
      </div>

      {/* Upload panel (collapsible) */}
      {showUpload && (
        <UploadPanel
          isDark={isDark}
          description={description}
          setDescription={setDescription}
          autoSummary={autoSummary}
          setAutoSummary={setAutoSummary}
          uploadSite={uploadSite}
          fileInputRef={fileInputRef}
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
        />
      )}

      {/* Sites list / card grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-400 border-t-brand-500" />
          </div>
        ) : sites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Globe className={cn('mb-3 h-10 w-10', isDark ? 'text-neutral-600' : 'text-neutral-300')} />
            <p className={cn('text-sm', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
              {hasActiveFilters || search ? 'No sites match current filters.' : 'No hosted sites yet'}
            </p>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={() => applyFilters({ status: '', siteType: '', dateRange: 'all' })}
                className={cn('mt-2 text-xs underline-offset-2 hover:underline', isDark ? 'text-brand-400' : 'text-brand-600')}
              >
                Reset filters
              </button>
            ) : !search && (
              <p className={cn('mt-1 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
                Upload an HTML file or ZIP archive to get a shareable link
              </p>
            )}
          </div>
        ) : view === 'list' ? (
          <div className="mx-auto max-w-3xl space-y-2">
            {sites.map((site) => (
              <HostedSiteRow key={site.id} site={site} {...sharedSiteProps} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map((site) => (
              <HostedSiteCard key={site.id} site={site} {...sharedSiteProps} />
            ))}
          </div>
        )}
      </div>

      {/* Preview panel (bottom) */}
      {previewSite && (
        <div className={cn('border-t', isDark ? 'border-white/[0.06]' : 'border-neutral-200')} style={{ height: '50vh' }}>
          <div className={cn('flex items-center gap-2 px-3 py-1.5', isDark ? 'bg-surface-1' : 'bg-white')}>
            <Globe className="h-3 w-3 text-brand-500" />
            <span className={cn('flex-1 truncate text-xs', isDark ? 'text-neutral-300' : 'text-neutral-600')}>
              {previewSite.filename}
            </span>
            <a href={sitePublicUrl(previewSite)} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-500 hover:underline">
              Open
            </a>
            <button
              onClick={() => setPreviewSite(null)}
              className={cn('text-xs', isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700')}
            >
              Close
            </button>
          </div>
          <iframe
            src={sitePublicUrl(previewSite)}
            title={previewSite.filename}
            className="h-full w-full border-0"
            sandbox="allow-scripts"
          />
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete site"
        description={`Delete "${deleteTarget?.filename}" and all its files? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  )
}
